import { useEffect, useRef, useState } from 'react'
import DatePicker from 'react-datepicker'
import classNames from 'classnames'

import { ReactComponent as SingleArrowIcon } from '../../static/icons/arrow-back.svg'
import { ReactComponent as CalendarIcon } from '../../static/icons/calendar-18.svg'
import { ReactComponent as CloseIcon } from '../../static/icons/close.svg'
import { ReactComponent as DoubleArrowIcon } from '../../static/icons/double-arrow.svg'
import { dateFormatter } from '../../utils/date'

import {
  calendarIconClasses,
  clearButtonClasses,
  inputClasses,
} from './DateField.constants'
import type { DateFieldProps } from './DateField.types'

import 'react-datepicker/dist/react-datepicker.css'

const DateField = (props: DateFieldProps) => {
  const {
    value,
    onChange,
    size = 'md',
    className = '',
    error = false,
    disabled,
  } = props
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const date = value ? new Date(value) : null
  const formattedValue = date ? dateFormatter.format(date) : ''

  const inputClassNames = classNames(
    inputClasses.base,
    inputClasses.size[size],
    className,
    inputClasses.status[error ? 'error' : 'success'],
    disabled && inputClasses.disabled
  )

  const calendarIconClassNames = classNames(
    calendarIconClasses.base,
    calendarIconClasses.status[error ? 'error' : 'success'],
    disabled && calendarIconClasses.disabled
  )

  const clearButtonClassNames = classNames(
    clearButtonClasses.base,
    clearButtonClasses.status[error ? 'error' : 'success'],
    disabled && calendarIconClasses.disabled
  )

  const handleClearClick = () => {
    if (!disabled) {
      onChange(null)
      setOpen(false)
    }
  }

  const handleDateChange = (date: Date | null) => {
    if (!disabled) {
      onChange(date ? date.toISOString() : null)
      setOpen(false)
    }
  }

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative flex items-center">
        <input
          readOnly
          type="text"
          value={formattedValue}
          placeholder="Select a date"
          className={inputClassNames}
          onClick={() => !disabled && setOpen(!open)}
        />

        {/* Calendar icon */}
        <div
          className={calendarIconClassNames}
          onClick={() => !disabled && setOpen(!open)}
        >
          <CalendarIcon className="h-5 w-5" />
        </div>

        {/* Clear button */}
        {value && (
          <button
            type="button"
            className={clearButtonClassNames}
            disabled={disabled}
            onClick={handleClearClick}
          >
            <CloseIcon className="h-3 w-3" />
          </button>
        )}
      </div>

      {open && !disabled && (
        <div className="absolute z-50 mt-2 w-full">
          <DatePicker
            inline
            selected={date}
            renderCustomHeader={({
              date,
              decreaseMonth,
              increaseMonth,
              decreaseYear,
              increaseYear,
              prevMonthButtonDisabled,
              nextMonthButtonDisabled,
              prevYearButtonDisabled,
              nextYearButtonDisabled,
            }) => (
              <div className="flex items-center justify-between px-2 py-1 select-none">
                {/* Left controls */}
                <div className="flex gap-1">
                  {/* Previous Year */}
                  <button
                    type="button"
                    disabled={prevYearButtonDisabled || disabled}
                    className="p-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                    onClick={decreaseYear}
                  >
                    <DoubleArrowIcon className="h-5 w-5 transform rotate-180" />
                  </button>

                  {/* Previous Month */}
                  <button
                    type="button"
                    disabled={prevMonthButtonDisabled || disabled}
                    className="p-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                    onClick={decreaseMonth}
                  >
                    <SingleArrowIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Month + Year label */}
                <span className="font-medium">
                  {date.toLocaleString('default', { month: 'long' })}{' '}
                  {date.getFullYear()}
                </span>

                {/* Right controls */}
                <div className="flex gap-1">
                  {/* Next Month */}
                  <button
                    type="button"
                    disabled={nextMonthButtonDisabled || disabled}
                    className="p-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                    onClick={increaseMonth}
                  >
                    <SingleArrowIcon className="h-5 w-5 transform rotate-180" />
                  </button>

                  {/* Next Year */}
                  <button
                    type="button"
                    disabled={nextYearButtonDisabled || disabled}
                    className="p-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                    onClick={increaseYear}
                  >
                    <DoubleArrowIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
            onChange={handleDateChange}
          />
        </div>
      )}
    </div>
  )
}

export default DateField
